// Lessons: library of strategy units per exam + the reading view for one unit.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, ArrowRight, BookOpen, Clock, Headphones, Mic, PenTool, PlayCircle } from 'lucide-react';

import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { langDir } from '@/lib/lang';
import AppShell from '@/components/AppShell';
import LessonCards from '@/components/LessonCards';
import { ExamScreenCard } from '@/components/ExamWalkthrough';
import { EXAM_SCREENS } from '@/data/examScreens';

const SECTION_META = {
  listening: { label: 'Listening', icon: Headphones },
  reading: { label: 'Reading', icon: BookOpen },
  writing: { label: 'Writing', icon: PenTool },
  speaking: { label: 'Speaking', icon: Mic },
};
const SECTION_ORDER = ['listening', 'reading', 'writing', 'speaking'];

export function LessonLibrary() {
  const { exam } = useParams();
  const { lang } = useApp();
  const [units, setUnits] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api.curriculum(exam, lang)
      .then((res) => setUnits(res.data.units))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load lessons.'));
  }, [exam, lang]);

  if (error) return (
    <AppShell>
      <p className="text-slate-600">{error}</p>
      <div className="mt-4 flex gap-3">
        <Link to={`/${exam}/lessons`} className="cta">All lessons</Link>
        <button type="button" onClick={() => window.location.reload()} className="cta-quiet">Try again</button>
      </div>
    </AppShell>
  );

  const bySection = {};
  units.forEach((u) => {
    if (!bySection[u.section]) bySection[u.section] = [];
    bySection[u.section].push(u);
  });

  return (
    <AppShell>
      <p className="mono-stat">{exam} · strategy lessons</p>
      <h1 className="h-display mt-3 max-w-lg text-3xl" data-testid="lessons-hub-hero">Learn the traps first.</h1>
      <p className="mt-3 max-w-lg text-[15px] leading-relaxed text-slate-500">
        Every task type has traps built to cost you points. These lessons name them, so on test
        day you recognize the trap before it takes your point.
      </p>
      <div className="mt-10 max-w-3xl space-y-10">
        {SECTION_ORDER.filter((s) => bySection[s]).map((section) => {
          const Icon = SECTION_META[section].icon;
          return (
            <section key={section}>
              <div className="flex items-center gap-2.5 pb-2">
                <Icon className="h-4 w-4 text-slate-400" />
                <h2 className="font-display text-base font-semibold text-slate-900">{SECTION_META[section].label}</h2>
                <span className="text-xs text-slate-400">{bySection[section].length} lessons</span>
              </div>
              <div className="border border-slate-200 bg-white">
                {bySection[section].sort((a, b) => a.order - b.order).map((u, i) => (
                  <Link
                    key={u.unit_id}
                    to={`/${exam}/lessons/${u.unit_id}`}
                    data-testid={`lesson-${u.unit_id}`}
                    className={`group flex items-center gap-4 px-5 py-4 transition-colors hover:bg-slate-50 ${i > 0 ? 'border-t border-slate-100' : ''}`}
                  >
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display text-[15px] font-semibold leading-snug text-slate-900 group-hover:text-indigo-700">{u.title}</h3>
                      {u.summary && <p className="mt-0.5 truncate text-sm text-slate-400">{u.summary}</p>}
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-indigo-600" />
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </AppShell>
  );
}

export function LessonReader() {
  const { exam, unitId } = useParams();
  const { lang } = useApp();
  const [unit, setUnit] = useState(null);
  const [walkthrough, setWalkthrough] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.unit(exam, unitId, lang)
      .then((res) => setUnit(res.data.unit))
      .catch((err) => setError(err.response?.data?.detail || 'Lesson not found.'));
  }, [exam, unitId, lang]);

  useEffect(() => {
    if (!unit?.task_type) return;
    api.walkthroughs(exam, lang)
      .then((res) => setWalkthrough((res.data.walkthroughs || []).find((w) => w.task_type === unit.task_type) || null))
      .catch(() => setWalkthrough(null));
  }, [exam, unit?.task_type, lang]);

  if (error) return (
    <AppShell>
      <p className="text-slate-600">{error}</p>
      <div className="mt-4 flex gap-3">
        <Link to={`/${exam}/lessons`} className="cta">All lessons</Link>
        <button type="button" onClick={() => window.location.reload()} className="cta-quiet">Try again</button>
      </div>
    </AppShell>
  );
  if (!unit) return <AppShell><p className="text-slate-400">Loading…</p></AppShell>;

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl" data-testid="lesson-reader">
        <Link
          to={`/${exam}/lessons`}
          className="mb-8 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
          data-testid="lesson-back"
        >
          <ArrowLeft className="h-4 w-4" /> All lessons
        </Link>
        <p className="mono-stat">{SECTION_META[unit.section]?.label || unit.section} · strategy</p>
        <h1 className="h-display mt-3 text-[32px] leading-[1.1]" dir={langDir(lang)}>{unit.title}</h1>
        {unit.summary && (
          <p className="mt-3 text-[15px] leading-relaxed text-slate-500" dir={langDir(lang)}>{unit.summary}</p>
        )}

        {EXAM_SCREENS[exam]?.[unit.task_type] && (
          <div className="mt-8" data-testid="lesson-screen-preview">
            <p className="mono-stat">On screen, this is what it looks like</p>
            <div className="mt-3">
              <ExamScreenCard exam={exam} taskType={unit.task_type} />
            </div>
          </div>
        )}

        <div className="mt-8 border-t border-slate-200 pt-8" data-testid="lesson-body" dir={langDir(lang)}>
          {unit.cards?.length > 0 ? (
            <LessonCards cards={unit.cards} />
          ) : (
            <article className="prose-custom">
              <ReactMarkdown>{unit.body_md}</ReactMarkdown>
            </article>
          )}
        </div>
        {(walkthrough || unit.task_type) && (
          <div className="mt-12 border-t border-slate-200 pt-8">
            <p className="mono-stat">when you’re ready</p>
            <div className="mt-4 space-y-3">
              {walkthrough && (
                <Link
                  to={`/${exam}/walkthrough/${walkthrough.walkthrough_id}`}
                  data-testid="lesson-walkthrough-cta"
                  className="group flex items-center gap-4 border border-indigo-200 bg-indigo-50/40 p-5 transition-colors hover:bg-indigo-50"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">1</span>
                  <PlayCircle className="h-7 w-7 shrink-0 text-indigo-600" />
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[15px] font-semibold text-slate-900">Watch it worked through · {walkthrough.step_count} steps</p>
                    <p className="mt-0.5 text-sm text-slate-500">See every trap caught one move at a time, then try it yourself.</p>
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0 text-indigo-400 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )}
              {unit.task_type && (
                <Link
                  to={`/${exam}/sets/${unit.task_type}`}
                  data-testid="lesson-practice-cta"
                  className="group flex items-center gap-4 border border-slate-300 bg-white p-5 transition-colors hover:border-slate-900"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-bold text-white">{walkthrough ? 2 : 1}</span>
                  <Clock className="h-7 w-7 shrink-0 text-slate-700" />
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[15px] font-semibold text-slate-900">Practice under the clock</p>
                    <p className="mt-0.5 text-sm text-slate-500">Pick a timed set. Every miss shows the trap behind it.</p>
                  </div>
                  <ArrowRight className="h-5 w-5 shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
